import React from "react";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import GenericSpecsHistoryExpandedContent, {
  type RevisionDiffState,
} from "../GenericSpecsHistoryExpandedContent";
import type { SpecsHistoryRevision } from "../../../service/SpecsRevisionService";
import { palette, spacing } from "../../../styles/theme";

export type { RevisionDiffState };

export type RevisionHistoryTableProps = {
  title: string;
  subtitle?: string;
  emptyMessage: string;
  revisions: SpecsHistoryRevision[];
  revisionDiffs: Record<string, RevisionDiffState>;
  loading: boolean;
  error: string | null;
  pageIndex: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  loadingRevisionId: string | null;
  activeRevisionId?: string | null;
  publishingRevisionId: string | null;
  specName: string;
  getStatusBadges?: (revision: SpecsHistoryRevision) => React.ReactNode;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onBack: (specName?: string) => void;
  onOpenRevision?: (revisionId: string) => Promise<void> | void;
  onExpandRevision?: (
    revision: SpecsHistoryRevision,
    fallbackRevisionId: string | null,
    compareAgainstBaseSpecs: boolean
  ) => void;
  onPublishRevision: (revision: SpecsHistoryRevision) => void;
};

function formatRevisionDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function renderSavedBy(value: string) {
  if (!value) return "unknown";
  return value;
}

export default function RevisionHistoryTable({
  title,
  subtitle,
  emptyMessage,
  revisions,
  revisionDiffs,
  loading,
  error,
  pageIndex,
  hasPreviousPage,
  hasNextPage,
  loadingRevisionId,
  activeRevisionId,
  publishingRevisionId,
  specName,
  getStatusBadges,
  onPreviousPage,
  onNextPage,
  onBack,
  onOpenRevision,
  onExpandRevision,
  onPublishRevision,
}: RevisionHistoryTableProps) {
  const [expandedRevisionId, setExpandedRevisionId] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    setExpandedRevisionId(null);
  }, [pageIndex, revisions]);

  return (
    <Box
      sx={{
        textAlign: "center",
      }}
    >
      <Stack
        spacing={spacing.groupingDesktop}
        sx={{
          width: "100%",
          margin: "auto",
          maxWidth: "1200px",
          px: 2.5,
          boxSizing: "border-box",
          textAlign: "left",
        }}
      >
        <Paper variant="outlined" sx={{ p: spacing.groupingDesktop }}>
          <Box sx={{ maxWidth: "1200px", margin: "auto", textAlign: "left" }}>
            <Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box>
                  <Typography variant="h2">{title}</Typography>
                  {subtitle ? (
                    <Typography variant="body2" color="text.secondary">
                      {subtitle}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            </Stack>
          </Box>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Box>
          {loading ? (
            <Box sx={{ py: spacing.groupingDesktop }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">
                  Loading revision history…
                </Typography>
              </Stack>
            </Box>
          ) : revisions.length === 0 ? (
            <Box sx={{ py: spacing.groupingDesktop }}>
              <Alert severity="info">{emptyMessage}</Alert>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Saved by</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revisions.map((revision, revisionIndex) => {
                    const isActive = activeRevisionId === revision.revisionId;
                    const isLoading = loadingRevisionId === revision.revisionId;
                    const isExpanded =
                      expandedRevisionId === revision.revisionId;
                    const previousRevision =
                      revisions[revisionIndex + 1] ?? null;

                    return (
                      <React.Fragment key={revision.revisionId}>
                        <TableRow
                          selected={isActive}
                          sx={{
                            "&.Mui-selected": {
                              bgcolor: palette.brand.paleEffect,
                            },
                          }}
                        >
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <IconButton
                                size="small"
                                aria-label="Expand revision details"
                                onClick={() => {
                                  setExpandedRevisionId((current) => {
                                    const isExpanded =
                                      current !== revision.revisionId;
                                    if (isExpanded) {
                                      const fallbackRevisionId =
                                        previousRevision?.revisionId ?? null;
                                      onExpandRevision?.(
                                        revision,
                                        fallbackRevisionId,
                                        !(
                                          revision.sourceRevisionId ??
                                          fallbackRevisionId
                                        )
                                      );
                                    }
                                    return isExpanded
                                      ? revision.revisionId
                                      : null;
                                  });
                                }}
                                sx={{
                                  transform: isExpanded
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                                  transition: "transform 0.2s ease",
                                }}
                              >
                                <ExpandMoreIcon fontSize="small" />
                              </IconButton>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {formatRevisionDate(revision.createdAt)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              flexWrap="wrap"
                            >
                              {revision.isCurrentPublished ? (
                                <Chip
                                  size="small"
                                  color="success"
                                  label="Published"
                                />
                              ) : null}
                              {revision.isCurrentDraft ? (
                                <Chip
                                  size="small"
                                  color="primary"
                                  label="Draft"
                                />
                              ) : null}
                              {getStatusBadges?.(revision)}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {renderSavedBy(revision.createdBy)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {revision.changeNote || "No note provided"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {onOpenRevision ? (
                              <Button
                                size="small"
                                variant={isActive ? "contained" : "outlined"}
                                startIcon={<ArrowBackOutlinedIcon />}
                                disabled={isLoading}
                                onClick={() => {
                                  void onOpenRevision(revision.revisionId);
                                }}
                              >
                                {isLoading
                                  ? "Opening…"
                                  : isActive
                                    ? "Current"
                                    : "Open"}
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            colSpan={5}
                            sx={{ borderBottom: 0, py: 0 }}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ py: 1.5 }}>
                                <GenericSpecsHistoryExpandedContent
                                  revision={revision}
                                  previousRevision={previousRevision}
                                  revisionDiffs={revisionDiffs}
                                  isExpanded={isExpanded}
                                  specName={specName}
                                  publishingRevisionId={publishingRevisionId}
                                  onBack={onBack}
                                  onPublish={onPublishRevision}
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Stack direction="row" justifyContent="space-between">
          <Button disabled={!hasPreviousPage} onClick={onPreviousPage}>
            Previous
          </Button>
          <Button disabled={!hasNextPage} onClick={onNextPage}>
            Next
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
